import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Fuel, Wrench, Shield, Zap, Car, Receipt, TrendingDown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function RealCost() {
  const [price, setPrice] = useState(60000);
  const [monthly, setMonthly] = useState(699);
  const [term, setTerm] = useState(36);
  const [isEv, setIsEv] = useState(false);
  const [vClass, setVClass] = useState("luxury_suv"); // economy_sedan, luxury_sedan, luxury_suv, sports, truck
  const [commute, setCommute] = useState(15000); // annual miles
  const [state, setState] = useState("nc"); // nc, ca, ny, tx, fl

  // Insurance estimates (monthly, US averages 2025) by class
  const insuranceBase = {
    economy_sedan: 130, luxury_sedan: 195, luxury_suv: 225, sports: 285, truck: 175,
  }[vClass];
  // Geo modifier
  const geoMod = { ca: 1.3, ny: 1.25, fl: 1.15, tx: 1.05, nc: 1.0 }[state];
  const insurance = Math.round(insuranceBase * geoMod);

  // Fuel / electric cost (monthly)
  const milesPerMonth = commute / 12;
  const mpg = { economy_sedan: 32, luxury_sedan: 24, luxury_suv: 21, sports: 18, truck: 19 }[vClass];
  const gasPrice = 3.6; // national avg
  const fuelGas = Math.round((milesPerMonth / mpg) * gasPrice);
  // EV: 0.35 kWh/mi avg, $0.16/kWh
  const fuelEv = Math.round(milesPerMonth * 0.35 * 0.16);
  const fuel = isEv ? fuelEv : fuelGas;

  // Maintenance (monthly accrual)
  const maint = isEv ? 35 : { economy_sedan: 65, luxury_sedan: 110, luxury_suv: 135, sports: 175, truck: 110 }[vClass];

  // Property tax — varies wildly; NC has annual ~3% of value; CA registration fees ~1%
  const propTaxAnnual = { nc: price * 0.013, ca: price * 0.0065, ny: 90, tx: 130, fl: 50 }[state];
  const propTaxMonthly = Math.round(propTaxAnnual / 12);

  // Warranty / Service contract amortized (optional add-on, $2,400 over 36 mo)
  const warranty = Math.round(2400 / term);

  const realMonthly = monthly + insurance + fuel + maint + propTaxMonthly + warranty;

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="real-cost-page">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-silver" /><span className="label-mono">Real Monthly Cost</span></div>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-3">What it actually costs.</h1>
      <p className="text-gray-400 mb-10 max-w-2xl">Sticker payment lies. Here's a real picture — insurance, fuel/electric, maintenance, property tax, and an optional warranty.</p>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 glass rounded-2xl p-7 space-y-7">
          <div>
            <div className="flex items-center justify-between mb-3"><div className="text-sm text-gray-300">Vehicle price</div><div className="font-display text-lg">${price.toLocaleString()}</div></div>
            <Slider min={20000} max={250000} step={1000} value={[price]} onValueChange={(v) => setPrice(v[0])} data-testid="rc-price" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-3"><div className="text-sm text-gray-300">Lease / loan payment</div><div className="font-display text-lg">${monthly}/mo</div></div>
            <Slider min={200} max={2500} step={25} value={[monthly]} onValueChange={(v) => setMonthly(v[0])} data-testid="rc-monthly" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-3"><div className="text-sm text-gray-300">Annual mileage</div><div className="font-display text-lg">{(commute/1000).toFixed(0)}k mi</div></div>
            <Slider min={5000} max={30000} step={1000} value={[commute]} onValueChange={(v) => setCommute(v[0])} data-testid="rc-miles" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="label-mono mb-2">Vehicle class</div>
              <select value={vClass} onChange={(e) => setVClass(e.target.value)} className="w-full bg-[#0A0A0E] border border-[#1A1A24] h-11 rounded-xl text-white px-3 text-sm" data-testid="rc-class">
                <option value="economy_sedan">Economy sedan</option>
                <option value="luxury_sedan">Luxury sedan</option>
                <option value="luxury_suv">Luxury SUV</option>
                <option value="sports">Sports / Coupe</option>
                <option value="truck">Truck</option>
              </select>
            </div>
            <div>
              <div className="label-mono mb-2">State</div>
              <select value={state} onChange={(e) => setState(e.target.value)} className="w-full bg-[#0A0A0E] border border-[#1A1A24] h-11 rounded-xl text-white px-3 text-sm" data-testid="rc-state">
                <option value="nc">North Carolina</option>
                <option value="ca">California</option>
                <option value="ny">New York</option>
                <option value="tx">Texas</option>
                <option value="fl">Florida</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={isEv} onChange={(e) => setIsEv(e.target.checked)} data-testid="rc-ev" />
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-emerald-400" /> Electric vehicle</span>
          </label>
        </div>

        <div className="lg:col-span-5">
          <div className="glass rounded-2xl p-7 text-center mb-5">
            <div className="label-mono mb-2">Real Monthly Cost</div>
            <div className="font-display text-6xl font-light" data-testid="rc-real-monthly">${realMonthly.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-2">vs sticker payment ${monthly}/mo</div>
            <div className="text-xs text-emerald-400 mt-1">${(realMonthly - monthly).toLocaleString()} hidden / month</div>
          </div>
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 space-y-3">
            <Row icon={Car} label="Payment" value={`$${monthly.toLocaleString()}`} />
            <Row icon={Shield} label="Insurance" value={`$${insurance}`} />
            <Row icon={isEv ? Zap : Fuel} label={isEv ? "Charging" : "Fuel"} value={`$${fuel}`} />
            <Row icon={Wrench} label="Maintenance" value={`$${maint}`} />
            <Row icon={Receipt} label="Tax / fees" value={`$${propTaxMonthly}`} />
            <Row icon={TrendingDown} label="Warranty (amortized)" value={`$${warranty}`} />
          </div>
          <Link to="/lease-deals" className="mt-5 block text-center silver-gradient text-[#050507] py-3 rounded-full font-bold text-sm">See deals within budget</Link>
        </div>
      </div>

      <p className="text-[10px] text-gray-500 mt-8 max-w-3xl">Estimates use US 2025 averages. Actual costs vary by zip, driving record, vehicle trim, and lender. Talk to a 555 broker for a personalized model.</p>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3 text-sm text-gray-300"><Icon className="w-4 h-4 text-silver" />{label}</div>
      <div className="font-display">{value}</div>
    </div>
  );
}
